import { Component, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

// Erkennt gescheiterte dynamische Imports (Lazy-Chunks). Tritt vor allem auf,
// wenn ein neues Deploy die Chunk-Hashes ändert, der Browser aber noch die alte
// index.html im Speicher hat und auf eine nicht mehr existierende Datei zeigt.
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk|Failed to fetch/i.test(msg);
}

const CHUNK_RELOAD_KEY = "sherloxaware_chunk_reload_at";

// Neu laden mit Cache-Buster. Ein schlichtes location.reload() darf der Browser
// aus dem Cache bedienen – dann kommt dieselbe alte index.html mit denselben
// verschwundenen Chunk-Namen zurueck und die Seite bleibt kaputt (in Safari
// zuverlaessig reproduzierbar). Ein veraenderter Query-Parameter erzwingt einen
// echten Roundtrip; replace() haelt die Historie sauber.
function reloadWithoutCache(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

type Props = { children: ReactNode; resetKey?: string };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: Props) {
    // Beim Navigieren (neue Route) die Fehleranzeige zurücksetzen, damit ein
    // Fehler auf Seite A nicht auf der frisch geöffneten Seite B hängen bleibt.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      // Genau einmal neu laden (frische index.html holen). Der Zeitstempel-Guard
      // verhindert eine Reload-Schleife, falls der Chunk dauerhaft fehlt.
      try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          reloadWithoutCache();
          return;
        }
      } catch {
        // sessionStorage nicht verfügbar – Fallback-UI zeigen
      }
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Render-Fehler abgefangen:", error);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "16px", padding: "2.5rem", maxWidth: "440px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", textAlign: "center" }}>
          <div style={{ width: "56px", height: "56px", background: "var(--danger-bg)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem" }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="var(--danger)" width="28" height="28">
              <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--text-primary)", margin: "0 0 0.5rem 0" }}>
            Diese Seite konnte nicht geladen werden
          </h1>
          <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", margin: "0 0 1.75rem 0", lineHeight: 1.6 }}>
            Beim Anzeigen dieser Seite ist ein Fehler aufgetreten. Bitte lade die Seite neu. Sollte das Problem bestehen bleiben, wende dich an den Support.
          </p>
          <button
            onClick={reloadWithoutCache}
            style={{ width: "100%", padding: "0.75rem", background: "#1B6AB1", color: "#ffffff", border: "none", borderRadius: "8px", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer" }}
          >
            Neu laden
          </button>
        </div>
      </div>
    );
  }
}

// Setzt die ErrorBoundary bei jedem Routenwechsel zurück (resetKey = Pfad).
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}
